import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { api, apiClient } from '../api/client';
import Modal from './Modal';
import NumericInput from './NumericInput';
import Button from './ui/Button';
import { ConfirmModal } from './ui';
import ToggleSwitch from './forms/ToggleSwitch';
import './MemberMemberships.css';
import { translateError } from '../utils/errorMessages';

interface IGroup {
  _id: string;
  name: string;
}

interface PriceTier {
  durationDays: number;
  price: number;
  label: string;
  sortOrder: number;
  isDefault: boolean;
}

interface CalendarConfig {
  startMonth: number;
  intervals: Partial<Record<'yearly' | 'half_year' | 'quarter' | 'monthly', number>>;
}

interface IMembershipInfo {
  _id: string;
  name: string;
  description?: string;
  type: 'calendar' | 'time';
  yearlyPrice: number;
  prices: PriceTier[];
  calendarConfig?: CalendarConfig;
  groups: IGroup[];
  paymentStatus: 'paid' | 'pending' | 'overdue' | 'paused' | null;
  enrollmentStatus: 'active' | 'paused' | 'expired' | 'removed' | null;
  periodStart: string | null;
  periodEnd: string | null;
  interval: string | null;
  outstandingAmount: number;
  autoRenew?: boolean;
  paidUntil?: string | null; // legacy fallback
}

interface MemberMembershipsProps {
  memberId: string;
  authToken: string;
  onTierChange?: () => void;
}

type IntervalKey = 'yearly' | 'half_year' | 'quarter' | 'monthly';

const INTERVAL_LABELS: Record<IntervalKey, string> = {
  yearly: 'memberships.intervalYearly',
  half_year: 'memberships.intervalHalfYear',
  quarter: 'memberships.intervalQuarter',
  monthly: 'memberships.intervalMonthly',
};

const INTERVAL_MONTHS: Record<IntervalKey, number> = {
  yearly: 12,
  half_year: 6,
  quarter: 3,
  monthly: 1,
};

const MemberMemberships: React.FC<MemberMembershipsProps> = ({ memberId, onTierChange }) => {
  const { t } = useTranslation();
  const [memberships, setMemberships] = useState<IMembershipInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Enroll modal state
  const [enrollModalOpen, setEnrollModalOpen] = useState(false);
  const [selectedMembership, setSelectedMembership] = useState<IMembershipInfo | null>(null);
  const [enrollLoading, setEnrollLoading] = useState(false);
  const [autoRenew, setAutoRenew] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  // Time-bound enrollment state
  const [startDate, setStartDate] = useState('');
  const [selectedDurationDays, setSelectedDurationDays] = useState<number | null>(null);

  // Calendar-bound enrollment state
  const [selectedInterval, setSelectedInterval] = useState<IntervalKey | null>(null);

  // Pause confirmation state
  const [pauseMembershipId, setPauseMembershipId] = useState<string | null>(null);
  const [pauseLoading, setPauseLoading] = useState(false);

  // Quick pay modal state
  const [quickPayModalOpen, setQuickPayModalOpen] = useState(false);
  const [quickPayMembership, setQuickPayMembership] = useState<IMembershipInfo | null>(null);
  const [quickPayAmount, setQuickPayAmount] = useState('');
  const [quickPayDate, setQuickPayDate] = useState('');
  const [quickPayLoading, setQuickPayLoading] = useState(false);

  const fetchMemberships = async () => {
    try {
      setLoading(true);
      const { data } = await apiClient.get<IMembershipInfo[]>(
        `/api/members/${memberId}/memberships`
      );
      setMemberships(data);
      setError(null);
    } catch (err: any) {
      setError(translateError(err, t));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (memberId) {
      fetchMemberships();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberId]);

  const openChangePeriodModal = (membership: IMembershipInfo) => {
    setSelectedMembership(membership);
    setStartDate(new Date().toISOString().split('T')[0]);
    setAutoRenew(membership.autoRenew || false);
    setIsEditing(true);

    // Pre-select current interval/tier
    if (membership.type === 'calendar' && membership.interval) {
      setSelectedInterval(membership.interval as IntervalKey);
      setSelectedDurationDays(null);
    } else {
      setSelectedInterval(null);
      setSelectedDurationDays(null);
    }

    setEnrollModalOpen(true);
  };

  const closeEnrollModal = () => {
    setEnrollModalOpen(false);
    setSelectedMembership(null);
    setStartDate('');
    setAutoRenew(false);
    setSelectedDurationDays(null);
    setSelectedInterval(null);
    setIsEditing(false);
  };

  const handleTierSelect = (tier: PriceTier) => {
    setSelectedDurationDays(tier.durationDays);
  };

  const computePeriodPreview = (): string => {
    if (!selectedMembership) return '';

    if (selectedMembership.type === 'calendar' && selectedInterval && selectedMembership.calendarConfig) {
      const startMo = selectedMembership.calendarConfig.startMonth;
      const months = INTERVAL_MONTHS[selectedInterval];
      const ref = new Date();
      const refYear = ref.getFullYear();

      // Generate candidate periods across 3 years (matching backend logic)
      const candidates: Array<{ year: number; month: number }> = [];
      for (let yearOffset = -1; yearOffset <= 1; yearOffset++) {
        const baseYear = refYear + yearOffset;
        for (let i = 0; i < 12; i += months) {
          let m = startMo + i;
          let y = baseYear;
          if (m > 12) { m -= 12; y += 1; }
          candidates.push({ year: y, month: m });
        }
      }

      // Find the period that contains today
      for (const cand of candidates) {
        const pStart = new Date(cand.year, cand.month - 1, 1);
        let endMonth = cand.month + months - 1;
        let endYear = cand.year;
        if (endMonth > 12) { endMonth -= 12; endYear += 1; }
        const pEnd = new Date(endYear, endMonth, 0);
        if (ref >= pStart && ref <= pEnd) {
          return `${pStart.toLocaleDateString()} – ${pEnd.toLocaleDateString()}`;
        }
      }
    }

    if (selectedMembership.type === 'time' && startDate && selectedDurationDays) {
      const start = new Date(startDate);
      const end = new Date(start);
      end.setDate(end.getDate() + selectedDurationDays - 1);
      return `${start.toLocaleDateString()} – ${end.toLocaleDateString()}`;
    }

    return '';
  };

  const handleEnrollMember = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedMembership) return;

    setEnrollLoading(true);
    try {
      if (isEditing) {
        // Change tier API
        const tierPayload: { interval?: string; durationDays?: number } = {};
        if (selectedMembership.type === 'calendar' && selectedInterval) {
          tierPayload.interval = selectedInterval;
        } else if (selectedMembership.type === 'time' && selectedDurationDays) {
          tierPayload.durationDays = selectedDurationDays;
        }
        await api.memberships.changeTier(selectedMembership._id, memberId, tierPayload);
      } else {
        const payload: any = { autoRenew };

        if (selectedMembership.type === 'calendar' && selectedInterval) {
          payload.interval = selectedInterval;
        } else if (selectedMembership.type === 'time') {
          if (startDate) payload.startDate = startDate;
          if (selectedDurationDays) payload.durationDays = selectedDurationDays;
        }

        await apiClient.post(
          `/api/members/${memberId}/memberships/${selectedMembership._id}`,
          payload
        );
      }
      closeEnrollModal();
      await fetchMemberships();
      if (isEditing && onTierChange) onTierChange();
    } catch (err: any) {
      setError(translateError(err, t));
    } finally {
      setEnrollLoading(false);
    }
  };

  const handlePauseConfirm = async () => {
    if (!pauseMembershipId) return;
    setPauseLoading(true);
    try {
      await api.memberships.removeMember(pauseMembershipId, memberId, 'pause');
      await fetchMemberships();
    } catch (err: any) {
      setError(translateError(err, t));
    } finally {
      setPauseLoading(false);
      setPauseMembershipId(null);
    }
  };

  const handleResumeMember = async (membershipId: string) => {
    try {
      await api.memberships.resumeMember(membershipId, memberId);
      await fetchMemberships();
    } catch (err: any) {
      setError(translateError(err, t));
    }
  };

  const openQuickPayModal = (membership: IMembershipInfo) => {
    setQuickPayMembership(membership);
    setQuickPayAmount(membership.outstandingAmount > 0 ? String(membership.outstandingAmount) : '');
    setQuickPayDate(new Date().toISOString().split('T')[0]);
    setQuickPayModalOpen(true);
  };

  const closeQuickPayModal = () => {
    setQuickPayModalOpen(false);
    setQuickPayMembership(null);
    setQuickPayAmount('');
    setQuickPayDate('');
  };

  const handleQuickPay = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!quickPayMembership) return;

    setQuickPayLoading(true);
    try {
      await api.payments.create({
        member: Number(memberId),
        membership: Number(quickPayMembership._id),
        amount: parseFloat(quickPayAmount),
        date: quickPayDate,
        description: quickPayMembership.name,
      });
      closeQuickPayModal();
      await fetchMemberships();
    } catch (err: any) {
      setError(translateError(err, t));
    } finally {
      setQuickPayLoading(false);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const formatPeriod = (membership: IMembershipInfo) => {
    if (membership.periodStart && membership.periodEnd) {
      return `${formatDate(membership.periodStart)} – ${formatDate(membership.periodEnd)}`;
    }
    // Legacy fallback
    if (membership.paidUntil) {
      return `${t('memberships.paidUntil')}: ${formatDate(membership.paidUntil)}`;
    }
    return '';
  };

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getPaymentStatusClass = (status: string | null) => {
    switch (status) {
      case 'paid': return 'status-paid';
      case 'pending': return 'status-expiring';
      case 'overdue': return 'status-expired';
      case 'paused': return 'status-expiring';
      default: return 'status-not-enrolled';
    }
  };

  const getPaymentStatusLabel = (status: string | null) => {
    switch (status) {
      case 'paid': return t('memberships.paymentPaid');
      case 'pending': return t('memberships.paymentPending');
      case 'overdue': return t('memberships.paymentOverdue');
      case 'paused': return t('memberships.statusPaused');
      default: return t('memberships.statusNotEnrolled');
    }
  };

  const getEnrollmentStatusClass = (status: string | null) => {
    switch (status) {
      case 'active': return 'status-paid';
      case 'paused': return 'status-expiring';
      case 'expired': return 'status-expired';
      case 'removed': return 'status-not-enrolled';
      default: return '';
    }
  };

  const getEnrollmentStatusLabel = (status: string | null) => {
    switch (status) {
      case 'active': return t('memberships.statusActive');
      case 'paused': return t('memberships.statusPaused');
      case 'expired': return t('memberships.statusExpiredEnrollment');
      case 'removed': return t('memberships.statusRemoved');
      default: return '';
    }
  };

  const getAvailableIntervals = (membership: IMembershipInfo): Array<{ key: IntervalKey; price: number }> => {
    if (!membership.calendarConfig?.intervals) return [];
    return (Object.entries(membership.calendarConfig.intervals) as Array<[IntervalKey, number]>)
      .filter(([, price]) => price > 0)
      .map(([key, price]) => ({ key, price }));
  };

  if (loading) {
    return <div className="member-memberships loading">{t('common.loading')}</div>;
  }

  if (error) {
    return <div className="member-memberships error">{t('common.error')}: {error}</div>;
  }

  if (memberships.length === 0) {
    return (
      <div className="member-memberships">
        <div className="memberships-header">
          <h4 className="memberships-title">{t('memberships.title')}</h4>
        </div>
        <div className="no-memberships">{t('memberships.noMembershipsAssigned')}</div>
      </div>
    );
  }

  const periodPreview = computePeriodPreview();

  return (
    <div className="member-memberships">
      <div className="memberships-header">
        <h4 className="memberships-title">{t('memberships.title')}</h4>
      </div>

      <div className="memberships-list">
        {memberships.map((membership) => (
          <div key={membership._id} className={`membership-item ${getPaymentStatusClass(membership.paymentStatus)}`}>
            <div className="membership-info">
              <div className="membership-name-row">
                <Link to={`/admin/memberships/${membership._id}`} className="membership-name">
                  {membership.name}
                </Link>
                {membership.enrollmentStatus && (
                  <span className={`membership-status-badge ${getEnrollmentStatusClass(membership.enrollmentStatus)}`}>
                    {getEnrollmentStatusLabel(membership.enrollmentStatus)}
                  </span>
                )}
                {/* The enrollment badge already explains a dead or paused row;
                    a second badge would repeat it or read a bogus "Not Enrolled". */}
                {membership.paymentStatus !== 'paused'
                  && (membership.paymentStatus || !membership.enrollmentStatus) && (
                  <span className={`membership-status-badge ${getPaymentStatusClass(membership.paymentStatus)}`}>
                    {getPaymentStatusLabel(membership.paymentStatus)}
                  </span>
                )}
              </div>
              <div className="membership-details">
                <span className="membership-price">
                  {membership.type === 'time'
                    ? `${formatAmount(membership.yearlyPrice)}/yr`
                    : t('memberships.typeCalendar')
                  }
                </span>
                {(membership.periodStart || membership.paidUntil)
                  && membership.enrollmentStatus !== 'removed'
                  && membership.enrollmentStatus !== 'expired' && (
                  <span className="membership-paid-until">
                    {formatPeriod(membership)}
                  </span>
                )}
              </div>
            </div>
            <div className="membership-actions">
              {(membership.enrollmentStatus === 'active' || membership.enrollmentStatus === 'paused') && membership.outstandingAmount > 0 && (
                <button
                  type="button"
                  className="membership-action-btn membership-action-btn--primary"
                  onClick={() => openChangePeriodModal(membership)}
                  title={t('memberships.changePeriod')}
                >
                  <i className="fas fa-edit" aria-hidden="true"></i>
                </button>
              )}
              {membership.enrollmentStatus === 'active' && (
                <button
                  type="button"
                  className="membership-action-btn membership-action-btn--warning"
                  onClick={() => setPauseMembershipId(membership._id)}
                  title={t('memberships.pause')}
                >
                  <i className="fas fa-pause" aria-hidden="true"></i>
                </button>
              )}
              {membership.enrollmentStatus === 'paused' && (
                <button
                  type="button"
                  className="membership-action-btn membership-action-btn--success"
                  onClick={() => handleResumeMember(membership._id)}
                  title={t('memberships.resume')}
                >
                  <i className="fas fa-play" aria-hidden="true"></i>
                </button>
              )}
              {(membership.paymentStatus === 'pending' || membership.paymentStatus === 'overdue') && (
                <button
                  type="button"
                  className="membership-action-btn membership-action-btn--pay"
                  onClick={() => openQuickPayModal(membership)}
                  title={t('memberships.quickPay')}
                >
                  💰
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Quick Pay Modal */}
      <Modal
        isOpen={quickPayModalOpen}
        onClose={closeQuickPayModal}
        title={t('memberships.quickPay')}
        size="small"
      >
        {quickPayMembership && (
          <form onSubmit={handleQuickPay} className="enroll-modal-form">
            <div className="enroll-modal-member">
              <strong>{quickPayMembership.name}</strong>
              {quickPayMembership.outstandingAmount > 0 && (
                <div className="enroll-modal-price">
                  {t('memberships.paymentPending')}: {formatAmount(quickPayMembership.outstandingAmount)} Kč
                </div>
              )}
            </div>
            <div className="form-group">
              <label htmlFor="quick-pay-amount">{t('payments.amount')}</label>
              <NumericInput
                id="quick-pay-amount"
                step="1"
                min="1"
                value={quickPayAmount}
                onChange={(e) => setQuickPayAmount(e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="quick-pay-date">{t('payments.date')}</label>
              <input
                type="date"
                id="quick-pay-date"
                value={quickPayDate}
                onChange={(e) => setQuickPayDate(e.target.value)}
                required
              />
            </div>
            <div className="enroll-modal-actions">
              <Button type="submit" fullWidth loading={quickPayLoading}>
                {quickPayLoading ? t('common.saving') : t('payments.addPayment')}
              </Button>
              <Button variant="secondary" fullWidth onClick={closeQuickPayModal}>
                {t('common.cancel')}
              </Button>
            </div>
          </form>
        )}
      </Modal>

      {/* Enroll / Change Period Modal */}
      <Modal
        isOpen={enrollModalOpen}
        onClose={closeEnrollModal}
        title={isEditing ? t('memberships.changePeriodTitle') : t('memberships.updateEnrollment')}
        size="medium"
      >
        {selectedMembership && (
          <form onSubmit={handleEnrollMember} className="enroll-modal-form">
            <div className="enroll-modal-member">
              <strong>{selectedMembership.name}</strong>
              <div className="enroll-modal-price">
                {selectedMembership.type === 'time'
                  ? `${formatAmount(selectedMembership.yearlyPrice)}/yr`
                  : t('memberships.typeCalendar')
                }
              </div>
            </div>

            {selectedMembership.type === 'calendar' ? (
              <>
                <div className="form-group">
                  <label>{t('memberships.selectInterval')}</label>
                  <div className="radio-cards">
                    {getAvailableIntervals(selectedMembership).map(({ key, price }) => (
                      <label
                        key={key}
                        className={`radio-card${selectedInterval === key ? ' selected' : ''}`}
                        onClick={() => setSelectedInterval(key)}
                      >
                        <input
                          type="radio"
                          name="interval"
                          value={key}
                          checked={selectedInterval === key}
                          onChange={() => setSelectedInterval(key)}
                        />
                        <div style={{ flex: 1, display: 'flex', justifyContent: 'space-between' }}>
                          <span>{t(INTERVAL_LABELS[key])}</span>
                          <span>{price.toLocaleString()} CZK</span>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {periodPreview && (
                  <div className="form-group">
                    <label>{t('memberships.periodPreview')}</label>
                    <div style={{ padding: '8px 12px', background: 'var(--bg-dark)', borderRadius: '4px', fontSize: '0.9rem' }}>
                      {periodPreview}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <>
                {!isEditing && (
                  <div className="form-group">
                    <label htmlFor="modal-start-date">{t('memberships.selectStartDate')}</label>
                    <input
                      type="date"
                      id="modal-start-date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      required
                    />
                  </div>
                )}

                {selectedMembership.prices.length > 0 ? (
                  <div className="quick-select-buttons">
                    {selectedMembership.prices
                      .sort((a, b) => a.sortOrder - b.sortOrder)
                      .map((tier, idx) => (
                        <button
                          key={idx}
                          type="button"
                          className={`quick-select-btn${selectedDurationDays === tier.durationDays ? ' selected' : ''}`}
                          onClick={() => handleTierSelect(tier)}
                          style={selectedDurationDays === tier.durationDays ? { borderColor: 'var(--primary-color)', background: 'var(--bg-medium)' } : undefined}
                        >
                          {tier.label} - {formatAmount(tier.price)}
                        </button>
                      ))}
                  </div>
                ) : (
                  <div className="quick-select-buttons">
                    <button type="button" className="quick-select-btn" onClick={() => handleTierSelect({ durationDays: 180, price: selectedMembership.yearlyPrice / 2, label: t('memberships.halfYear'), sortOrder: 1, isDefault: false })}>
                      {t('memberships.halfYear')}
                    </button>
                    <button type="button" className="quick-select-btn" onClick={() => handleTierSelect({ durationDays: 365, price: selectedMembership.yearlyPrice, label: t('memberships.fullYear'), sortOrder: 2, isDefault: true })}>
                      {t('memberships.fullYear')}
                    </button>
                  </div>
                )}

                {periodPreview && (
                  <div className="form-group">
                    <label>{t('memberships.periodPreview')}</label>
                    <div style={{ padding: '8px 12px', background: 'var(--bg-dark)', borderRadius: '4px', fontSize: '0.9rem' }}>
                      {periodPreview}
                    </div>
                  </div>
                )}
              </>
            )}

            {!isEditing && (
              <div className="form-group" style={{ marginTop: '0.5rem' }}>
                <div className="interval-row">
                  <ToggleSwitch checked={autoRenew} onChange={setAutoRenew} />
                  <span className="interval-row__name">{t('memberships.autoRenew')}</span>
                </div>
                <small className="auto-renew-desc">{t('memberships.autoRenewDesc')}</small>
              </div>
            )}

            <div className="enroll-modal-actions">
              <Button type="submit" fullWidth loading={enrollLoading}>
                {enrollLoading ? t('common.saving') : (isEditing ? t('memberships.changePeriod') : t('memberships.enrollConfirm'))}
              </Button>
              <Button variant="secondary" fullWidth onClick={closeEnrollModal}>
                {t('common.cancel')}
              </Button>
            </div>
          </form>
        )}
      </Modal>

      <ConfirmModal
        isOpen={!!pauseMembershipId}
        onClose={() => setPauseMembershipId(null)}
        onConfirm={handlePauseConfirm}
        title={t('common.confirmTitle')}
        message={t('memberships.confirmPause')}
        confirmText={t('memberships.pause')}
        cancelText={t('common.cancel')}
        variant="default"
        loading={pauseLoading}
      />
    </div>
  );
};

export default MemberMemberships;
